import { useEffect, useState, useCallback, useRef } from "react";
// Frameworks
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CourseCard from "components/Courses/CourseCard";
import { useHistory } from "react-router";
import useRobot from "hooks/useRobot";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// ✅ un solo wrapper
import DashboardTopMenu from "components/Dashboard/DashboardTopMenu";
import DashboardCompetitionCard from "components/Dashboard/DashboardCompetitionCard";
import DashboardEconomicCalendarCard from "components/Dashboard/DashboardEconomicCalendarCard";

import { LoadingButton } from "@mui/lab";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { resetBuilder } from "store/reducers/builder.reducer";
import { reset } from "store/reducers/strategy.reducer";
import { percentage } from "lib/helpers";
import { useTranslation } from "react-i18next";

// Assets
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TimelineIcon from "@mui/icons-material/Timeline";

// Custom components
import ProfitRiskBox from "components/common/ProfitRiskBox";
import CustomDonutChart from "components/common/DonutChart";
import LoadingShow from "components/common/LoadingShow";
import StrategyCard from "components/common/StrategyCard";
import ProtectedComponent from "components/common/ProtectedComponent";
import { Link, Redirect } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import useDispatch from "hooks/useAppDispatch";
import useStrategy from "hooks/useStrategy";
import { show } from "store/reducers/notifications.reducer";
import customAxios from "lib/axios";
import useOrientation from "hooks/useOrientation";

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(0, 1),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(0, 0, 0, 1),
    },
  },
  chartContainer: {
    [theme.breakpoints.up("lg")]: {
      height: "50%",
    },
  },
  lastStrategyContainer: {
    [theme.breakpoints.up("lg")]: {
      width: "30%",
      borderRadius: "10px 0 0 0",
    },
  },
  dataContainer: {
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },
  },
  pieContainer: {
    [theme.breakpoints.up("sm")]: {
      width: "50%",
    },
  },
  legendContainer: {
    [theme.breakpoints.up("sm")]: {
      width: "50%",
    },
  },
  myStrategiesContainer: {
    [theme.breakpoints.up("lg")]: {
      width: "70%",
      borderRadius: "10px 0 0 0",
    },
  },
  strategyContainer: {
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },
  },

  leftSummaryContainer: {
    [theme.breakpoints.up("lg")]: {
      borderRadius: "10px 0 0 10px",
      /* height: "100%", */
      width: "50%",
      margin: 0,
    },
  },
  leftContainer: {
    [theme.breakpoints.up("lg")]: {
      flexDirection: "row",
      width: "70%",
      height: "100%",
      margin: theme.spacing(0, 0, 0, 0),
    },
  },

  dashboardRow: {
    [theme.breakpoints.up("lg")]: {
      flexDirection: "row",
    },
  },
}));

// URL WS según entorno (mismo código que en TradingRobots.jsx)
const env =
  process.env.REACT_APP_ENVIRONMENT ||
  (window?.location.hostname === "localhost" ? "LOCAL" : "TEST");
const wsUrl =
  env === "LOCAL"
    ? "ws://localhost:8000"
    : env === "LIVE"
    ? "wss://restapi.tradeasy.tech"
    : "wss://testapi.tradeasy.tech";

const Dashboard = function () {
  const classes = useStyles();
  const theme = useTheme();
  const { isDesktop } = useOrientation();
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactLayout = !isDesktop || isNarrowViewport;
  const dispatch = useDispatch();
  const { load: loadStrategy } = useStrategy();
  const [ctaLoading, setCtaLoading] = useState(false);
  const isUserFree = localStorage.getItem("type_user") === "F";
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [sharedStrategies, setSharedStrategies] = useState([]);
  const [sortType, setSortType] = useState("robots");
  const [loadDestination, setLoadDestination] = useState("/builder");
  const [loaded, setLoaded] = useState(false);
  const [t, i18n] = useTranslation(["dashboard", "timeframes", "errors"]);
  const [PrimaryAccountInfo, setPrimaryAccount] = useState([]);
  const [sharedRobots, setSharedRobots] = useState([]);
  const [userCourse, setUserCourse] = useState([]);
  const [rankingTop3, setRankingTop3] = useState([]);
  const [rankingTop3LastUpdated, setRankingTop3LastUpdated] = useState(null);
  const [primaryAccountLoading, setPrimaryAccountLoading] = useState(true);
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);

  // === Formación (dashboard) ===
  const history = useHistory();
  const [progressMap, setProgressMap] = useState({});
  const [currentModuleMap, setCurrentModuleMap] = useState({});
  const userId = localStorage.getItem("user_id");

  // dentro del componente Dashboard (el mismo donde pintas la lista)
  const apiBase = process.env.REACT_APP_TRADEASY_API || ""; // base URL para customAxios

  const { load: loadRobot } = useRobot();

  // === Estado y handlers del menú (igual que Torneo) ===
  const [menuAnchorTop, setMenuAnchorTop] = useState(null);
  const [selectedTopItem, setSelectedTopItem] = useState(null);

  const closeTopMenu = useCallback(() => {
    setMenuAnchorTop(null);
    setSelectedTopItem(null);
  }, []);

  const openTopMenu = useCallback((e, rawItem) => {
    e.stopPropagation?.();

    // reutiliza lo que ya venga y solo rellena si falta
    const kind = rawItem?.kind || detectKind(rawItem, "robot");

    const sharedIdFromObj = getSharedId(rawItem);
    const finalId =
      sharedIdFromObj != null
        ? sharedIdFromObj
        : rawItem?.id != null
        ? rawItem.id
        : undefined;

    if (!finalId) {
      console.warn(
        "[Dashboard Top3] No se pudo detectar sharedId/id para item:",
        rawItem
      );
    }

    setSelectedTopItem({
      ...rawItem,
      kind,
      id: Number(finalId),
    });
    setMenuAnchorTop(e.currentTarget);
  }, []);

  const handleSharedLoad = useCallback(
    (id, destination) => async () => {
      setCtaLoading(true);
      if (mountedRef.current) setCtaLoading(true);
      try {
        if (sortType === "robots") {
          // sharedId -> robotId real
          const { data: sharedRobot } = await customAxios.get(
            `${apiBase}sharedRobots/${id}`
          );
          const robotId = sharedRobot?.robotId;
          if (!robotId) throw new Error("RobotId not found");

          // Cargar robot y navegar (igual que en Torneo)
          await loadRobot(robotId, true);
          history.push("/robotvalidation");
        } else {
          // Estrategias compartidas
          await loadStrategy("S", id);
          history.push(destination === "/builder" ? "/builder" : "/validation");
        }
      } catch (error) {
        console.error(error);
        const key = sortType === "robots" ? "loadRobot" : "loadStrategy";
        dispatch(
          show({
            type: "error",
            msg: t(`errors:${key}`) || error?.message || "Error loading",
          })
        );

        if (mountedRef.current) {
          dispatch(
            show({
              type: "error",
              msg: t(`errors:${key}`) || error?.message || "Error loading",
            })
          );
        }
      } finally {
        setCtaLoading(false);
        if (mountedRef.current) setCtaLoading(false);
      }
    },
    [apiBase, sortType, loadRobot, loadStrategy, history, dispatch, t]
  );

  // Detecta tipo de item igual que en Torneo
  const detectKind = (it, fallback = "robot") => {
    if (it?.kind) return it.kind;
    if (it?.refType === "S" || it?.REF_TIPO === "S" || it?.REF_TYPE === "S")
      return "strategy";
    if (it?.refType === "R" || it?.REF_TIPO === "R" || it?.REF_TYPE === "R")
      return "robot";
    if (it?.isRobot) return "robot";
    if (it?.isStrategy) return "strategy";
    if (it?.strategyId != null || it?.strategyName != null) return "strategy";
    if (
      it?.robotId != null ||
      it?.NOMBRE_ROBOT != null ||
      it?.robotName != null
    )
      return "robot";
    return fallback;
  };

  // Intenta sacar el ID compartido igual que en Tournament
  // Intenta sacar el ID compartido igual que en Tournament, pero SIN confundirlo con el id interno
  const getSharedId = (it = {}) => {
    // 1) si ya viene un sharedId explícito, úsalo
    if (Number.isFinite(Number(it.sharedId))) return Number(it.sharedId);

    // 2) campos típicos de "refId" / "id compartido"
    const keys = [
      "REF_ID",
      "REFID",
      "REF_ID_ROBOT",
      "REF_ID_STRATEGY",
      "refId",
      "ref_id",
      "ID_SHARED",
      "IDSHARED",
      "SHARED_ID",
      "SHARE_ID",
      "ID_SHARED_ROBOT",
      "SHARED_ROBOT_ID",
      "ROBOT_SHARED_ID",
      "ID_SHARED_STRATEGY",
      "SHARED_STRATEGY_ID",
      "STRATEGY_SHARED_ID",
      "IDSHARE",
      "ID_COMPARTIDO",
      "IDCOMPARTIDO",
      "REF",
      "ref", // a veces lo llaman así
    ];

    for (const k of keys) {
      const v = it?.[k];
      if (Number.isFinite(Number(v))) return Number(v);
    }

    // 3) SOLO como último recurso, usa .id (por si el backend no tiene nada más)
    if (Number.isFinite(Number(it.id))) return Number(it.id);

    return undefined;
  };

  const getTitle = (it) =>
    it?.robotName ||
    it?.strategyName ||
    it?.NOMBRE_ROBOT ||
    it?.name ||
    it?.title ||
    "";

  const calculateYearlyProfit = (profit, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = differenceInDays(start, end);
    const years = days / 365.25;

    // Evitamos la division por cero en caso de que sea el mismo ano
    if (years === 0) {
      return profit;
    }
    return profit / years;
  };

  const getAnnualizedProfit = (item) => {
    const profit = Number(item?.profit ?? 0);
    const startDate =
      item?.startDate ?? item?.start_date ?? item?.fromDate ?? item?.start;
    const endDate =
      item?.endDate ?? item?.end_date ?? item?.toDate ?? item?.end;

    if (!startDate || !endDate || !Number.isFinite(profit)) {
      return profit;
    }

    return calculateYearlyProfit(profit, endDate, startDate);
  };

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reloadDashboardData = useCallback(async () => {
    const loadMainData = async () => {
      let success = true;
      setLoadingScreen(true);
      try {
        // 👉 TODO menos la info de la cuenta
        const [
          sharedStrategiesRes,
          sharedRobotsRes,
          userCourseRes,
          tournamentRankingRes,
        ] = await Promise.all([
          customAxios("/sharedStrategies/truncatedRow"),
          customAxios("/sharedRobots/truncatedRow"),
          customAxios("/userCourse/user/courses"),
          customAxios(`/tournament/ranking/listActiveWithDetails`),
        ]);

        if (!mountedRef.current) return;

        setSharedStrategies(sharedStrategiesRes.data);
        setSharedRobots(sharedRobotsRes.data);
        setUserCourse(userCourseRes.data);

        // --- ranking top3 normalizado (igual que tenías) ---
        const list = Array.isArray(tournamentRankingRes?.data?.items)
          ? tournamentRankingRes.data.items
          : [];

        const top3Normalized = list
          .map((it, i) => {
          const rk = it?.ranking || it || {};
          const d = it?.detail || {};

          const kind =
            (it?.refType || rk?.STRAT_OR_ROBOT) === "R"
              ? "robot"
              : (it?.refType || rk?.STRAT_OR_ROBOT) === "S"
              ? "strategy"
              : "robot";

          const sharedId =
            it?.refId ?? rk?.REF_ID ?? d?.id ?? d?._id ?? null;

          const displayTitle =
            rk.NOMBRE_ROBOT ??
            rk.NOMBRE_ESTRATEGIA ??
            rk.NOMBRE ??
            d.title ??
            d.strategyName ??
            d.robotName ??
            "";

          return {
            raw: it,
            kind,
            id: sharedId ? Number(sharedId) : undefined,

            position: Number(rk.POSICION ?? i + 1),
            positionView: rk.POSICION_VIEW ?? String(rk.POSICION ?? i + 1),

            author: rk.AUTOR ?? d?.user?.name ?? "",

            title: displayTitle,
            robotName: kind === "robot" ? displayTitle : "",
            strategyName: kind === "strategy" ? displayTitle : "",

            score: Number(rk.SCORE ?? d?.score ?? 0),
            profitPct: Number(rk.PROFIT ?? d?.profit * 100 ?? 0),
            riskPct: Number(rk.DRAWDOWN ?? d?.relativeDrawdown * 100 ?? 0),

            asset: rk.ACTIVO ?? d?.asset ?? "",
            tf: rk.TF ?? d?.timeframe ?? "",

            updatedAt: rk.ULTIMA_ACTUALIZACION ?? d?.updatedAt ?? null,

            shared: it?.clientShared ?? it?.shared ?? 1,
            userId: d?.userId ?? d?.user_id ?? null,
          };
          })
          .sort((a, b) => a.position - b.position)
          .slice(0, 3);

        let maxTsTop3 = 0;
        top3Normalized.forEach((row) => {
          if (!row.updatedAt) return;
          const d = new Date(row.updatedAt);
          if (Number.isNaN(d.getTime())) return;
          const ts = d.getTime();
          if (Number.isFinite(ts)) {
            maxTsTop3 = Math.max(maxTsTop3, ts);
          }
        });

        setRankingTop3(top3Normalized);
        setRankingTop3LastUpdated(maxTsTop3 > 0 ? new Date(maxTsTop3) : null);
      } catch (error) {
        success = false;
        if (mountedRef.current) {
          setSharedStrategies([]);
          setSharedRobots([]);
          setUserCourse([]);
          setRankingTop3([]);
          setRankingTop3LastUpdated(null);
          dispatch(show({ type: "error", msg: t("errors:serverError") }));
        }
      } finally {
        if (mountedRef.current) {
          setLoadingScreen(false);
        }
      }

      return success;
    };

    const loadPrimaryAccount = async () => {
      let success = true;
      try {
        if (mountedRef.current) {
          setPrimaryAccountLoading(true); // ⬅️ empieza carga panel azul
        }

        const infoAccountRes = await customAxios(
          "/manageTradingAccount/getInfoPrimaryAccount"
        );
        /* console.log("datos del endpoint: ", infoAccountRes); */

        if (!mountedRef.current) return;
        setPrimaryAccount(infoAccountRes.data);
      } catch (e) {
        success = false;
        console.error(
          "[Dashboard] Error en /manageTradingAccount/getInfoPrimaryAccount:",
          e
        );
        if (mountedRef.current) {
          setPrimaryAccount([]);
        }
      } finally {
        if (mountedRef.current) {
          setPrimaryAccountLoading(false); // ⬅️ termina carga panel azul
        }
      }

      return success;
    };

    const [mainOk, primaryOk] = await Promise.all([
      loadMainData(),
      loadPrimaryAccount(),
    ]);

    if (mountedRef.current) {
      setInitialLoadFailed(!(mainOk && primaryOk));
    }
  }, [dispatch, t]);

  useEffect(() => {
    reloadDashboardData();
  }, [reloadDashboardData]);

  useEffect(() => {
    const handleOnline = () => {
      if (!initialLoadFailed) return;
      reloadDashboardData();
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [initialLoadFailed, reloadDashboardData]);

  const handleSortChange = (event, target) => {
    setSortType(target);
  };

  // justo debajo de handleSortChange, antes del return(
  const sharedHeaderTitle =
    sortType === "robots"
      ? t("dashboard:sharedRobotsHeader", "Robots compartidos")
      : t("dashboard:sharedStrategiesHeader", "Estrategias Compartidas");

  // 👉 actualiza el CTA para apuntar a la pestaña "compartidos"
  const sharedHeaderCta =
    sortType === "robots" ? "/robots?view=shared" : "/strategies?view=shared";

  // === Progreso + módulo actual SOLO para el panel "Formación" (cursos id 1 y 2)
  useEffect(() => {
    // si aún no tenemos los cursos del usuario, no hacemos nada
    if (!Array.isArray(userCourse) || userCourse.length === 0) return;

    // Solo mostramos estos dos cursos en el panel
    const visible = userCourse.filter((c) => [1, 2].includes(Number(c.id)));
    if (visible.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Progreso por curso
        const progressRes = await Promise.all(
          visible.map((c) =>
            customAxios.get(`/userCourseProgress/user/course/${c.id}`)
          )
        );
        const pmap = {};
        visible.forEach((c, i) => {
          pmap[c.id] = progressRes[i]?.data ?? {
            completed: 0,
            total: 0,
            percentage: 0,
          };
        });

        // 2) Módulo actual por curso
        const modsRes = await Promise.all(
          visible.map((c) => customAxios.get(`/modules/${c.id}`))
        );
        const cmap = {};
        visible.forEach((c, i) => {
          const list = Array.isArray(modsRes[i]?.data) ? modsRes[i].data : [];
          const completed = pmap[c.id]?.completed || 0;
          const idx = Math.min(completed, Math.max(list.length - 1, 0));
          const mod = list[idx] || {};
          cmap[c.id] = {
            es: mod.title || "",
            en: mod.titleEN || mod.title || "",
          };
        });

        if (!cancelled) {
          setProgressMap(pmap);
          setCurrentModuleMap(cmap);
        }
      } catch (_e) {
        // silencioso en el dashboard
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userCourse, userId, i18n?.language]);

  // === Lista final para el panel (solo id 1 y 2). La usaremos en el render (paso C).
  const trainingCourses = (userCourse ?? [])
    .filter((c) => [1, 2].includes(Number(c.id)))
    .map((c) => {
      const url =
        Number(c.id) === 1
          ? "https://web.tradeasy.tech/curso/"
          : "/myCourses/2";

      return {
        course: {
          ...c,
          progress: progressMap[c.id] || {
            completed: 0,
            total: 0,
            percentage: 0,
          },
          currentModule: currentModuleMap[c.id]?.es || "",
          currentModuleEN: currentModuleMap[c.id]?.en || "",
          link: url, // si no está asignado, CourseCard abrirá este link
        },
      };
    });

  // Navegación cuando el curso está asignado (CourseCard llama onClick)
  const handleCourseClick = (id) => history.push(`/myCourses/${id}`);

  // --- Estado WS panel azul (saldo / rentabilidad latente / órdenes abiertas) ---
  const [wsBalance, setWsBalance] = useState(null); // saldo actual vía WS
  const [wsCurrentProfitPct, setWsCurrentProfitPct] = useState(null); // rentabilidad latente %
  const [wsOpenOrders, setWsOpenOrders] = useState(null); // nº órdenes abiertas vía WS
  const [wsEquity, setWsEquity] = useState(null); // equity vía WS

  const wsDashboardRef = useRef(null);

  // === WebSocket para el panel azul (saldo / rent. latente / órdenes abiertas) ===
  useEffect(() => {
    const token = localStorage.getItem("user_id"); // igual que en TradingRobots.jsx

    // 👇 ID interno que usa el WS en accountId
    const primaryAccountId = PrimaryAccountInfo?.accountId;

    // si falta algo, no abrimos WS
    if (!token || !primaryAccountId) return;

    let closedByEffect = false;
    let ws = null;

    const parseWSData = async (data) => {
      if (typeof data === "string") return JSON.parse(data);
      if (data instanceof Blob) return JSON.parse(await data.text());
      if (data instanceof ArrayBuffer) {
        const text = new TextDecoder("utf-8").decode(data);
        return JSON.parse(text);
      }
      if (data && typeof data === "object") return data;
      return JSON.parse(String(data));
    };

    const connect = () => {
      if (closedByEffect) return;

      ws = new WebSocket(
        `${wsUrl}/orderProfits?accountId=${encodeURIComponent(
          primaryAccountId
        )}`,
        ["bearer", token]
      );

      wsDashboardRef.current = ws;
      ws.binaryType = "blob";

      ws.onopen = () => {
        try {
          // envía interés solo a esa cuenta
          ws.send(
            JSON.stringify({
              type: "interest",
              accounts: [primaryAccountId], // ✅ mismo valor que viene en messageObj.accountI
            })
          );
        } catch (e) {
          console.error("[Dashboard WS] Error enviando interest:", e);
        }
      };

      ws.onmessage = async (event) => {
        try {
          const messageObj = await parseWSData(event.data);
          /* console.log("[Dashboard WS] parsed message:", messageObj); */

          const { type, accountId: msgAccountId, data } = messageObj || {};
          if (type === "status") return;

          const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
          const toNum = (x) => Number(x);

          // --- AccountSummary: saldo + beneficio latente ---
          if (type === "AccountSummary") {
            // 👇 SOLO aquí filtramos por cuenta, y solo si viene el accountId
            /*  console.log(messageObj); */
            if (
              msgAccountId != null &&
              String(msgAccountId) !== String(primaryAccountId)
            ) {
              return; // mensaje de otra cuenta, por si acaso
            }

            const balanceNum = round2(
              messageObj.balance ?? messageObj.data?.balance
            );

            const equityNum = round2(
              messageObj.equity ?? messageObj.data?.equity
            );

            const profitAbs = round2(
              messageObj.profit ?? messageObj.data?.profit ?? 0
            );

            const profitPct =
              typeof messageObj.currentProfitPct !== "undefined"
                ? round2(messageObj.currentProfitPct)
                : balanceNum > 0
                ? (profitAbs / balanceNum) * 100
                : 0;

            if (!Number.isNaN(balanceNum)) setWsBalance(balanceNum);
            if (!Number.isNaN(equityNum)) setWsEquity(equityNum);
            setWsCurrentProfitPct(profitPct);

            const openCount =
              messageObj.openOrdersCount ??
              messageObj.openedOrdersCount ??
              data?.openOrdersCount;

            if (typeof openCount !== "undefined") {
              setWsOpenOrders(Number(openCount) || 0);
            }
          }

          // --- Mensajes donde vienen datos en data.* ---
          if (
            data &&
            (typeof data.balance !== "undefined" ||
              typeof data.profit !== "undefined")
          ) {
            const balanceNum = round2(data.balance ?? 0);
            const profitAbs = round2(data.profit ?? 0);

            const profitPct =
              balanceNum > 0 ? (profitAbs / balanceNum) * 100 : 0;

            if (!Number.isNaN(balanceNum)) setWsBalance(balanceNum);
            setWsCurrentProfitPct(profitPct);
          }

          // --- Órdenes abiertas por tickets ---
          // 👇 OJO: aquí ya NO filtramos por msgAccountId, porque:
          // - el socket ya está suscrito solo a primaryAccountId
          // - muchos mensajes no traen accountId
          if (type === "OpenedOrdersTickets" && data) {
            const tickets = Array.isArray(data) ? data.map(toNum) : [];
            setWsOpenOrders(tickets.length);
          }

          if (
            (type === "OrderProfitUpdate" || type === "OrderProfitDelta") &&
            data?.openedOrdersTickets
          ) {
            const ticketsArray = data.openedOrdersTickets.map(toNum);
            setWsOpenOrders(ticketsArray.length);
          }
        } catch (e) {
          // silencioso como lo tenías
        }
      };

      ws.onerror = (err) => {
        /*  console.error("[Dashboard WS] Error:", err); */
      };

      ws.onclose = () => {
        wsDashboardRef.current = null;

        if (!closedByEffect) {
          // reintento simple
          setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      closedByEffect = true;
      if (wsDashboardRef.current) {
        try {
          wsDashboardRef.current.close();
        } catch (_e) {
          // El socket puede estar ya cerrado, ignoramos el error
        }
      }
    };
  }, [PrimaryAccountInfo?.accountId]);

  // 🔄 Refrescar datos de la cuenta (últimos 30 días, mejor robot, etc.)
  // cada vez que CAMBIA de verdad el nº de órdenes abiertas (WS)
  // 🔄 Control de refrescos por WS
  const lastFetchedOpenOrdersRef = useRef(null);
  const skipFirstWsRefreshRef = useRef(true); // saltar primera vez (ya cubre la llamada inicial)
  const isRefreshingAccountRef = useRef(false); // flag: hay un refresh en marcha
  const lastAccountRefreshTsRef = useRef(0); // última vez que refrescamos (timestamp ms)

  // 🔄 Refrescar datos de la cuenta (últimos 30 días, mejor robot, etc.)
  // cada vez que CAMBIA de verdad el nº de órdenes abiertas (WS)
  useEffect(() => {
    const accountId = PrimaryAccountInfo?.account;
    if (!accountId) return; // si no hay cuenta, no hacemos nada
    if (wsOpenOrders == null) return; // evitamos primer render sin datos

    // ⛔️ Primera vez que entra con un valor de wsOpenOrders
    // (ya tenemos la info del Promise.all inicial) -> no llamamos al back
    if (skipFirstWsRefreshRef.current) {
      skipFirstWsRefreshRef.current = false;
      lastFetchedOpenOrdersRef.current = wsOpenOrders;
      return;
    }

    // 🛑 Si ya hicimos una petición con este mismo valor, no repetimos
    if (lastFetchedOpenOrdersRef.current === wsOpenOrders) {
      return;
    }
    lastFetchedOpenOrdersRef.current = wsOpenOrders;

    // 🧊 Throttle: no refrescar más de una vez cada 1500 ms
    const now = Date.now();
    const MIN_INTERVAL_MS = 1500;
    if (now - lastAccountRefreshTsRef.current < MIN_INTERVAL_MS) {
      // demasiado pronto desde el último refresh → no hacemos nada
      return;
    }

    // 👇 marcamos timestamp del intento (para agrupar ráfagas)
    lastAccountRefreshTsRef.current = now;

    // 🛡 Flag: si ya hay un refresh en marcha, no lanzamos otro
    if (isRefreshingAccountRef.current) {
      return;
    }
    isRefreshingAccountRef.current = true;

    let cancelled = false;
    let timer = null;

    // pequeño debounce por si el WS manda varias cosas seguidas
    timer = setTimeout(async () => {
      try {
        const res = await customAxios(
          "/manageTradingAccount/getInfoPrimaryAccount"
        );

        if (!cancelled) {
          setPrimaryAccount(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(
            "[Dashboard] Error refrescando getInfoPrimaryAccount:",
            e
          );
        }
      } finally {
        // liberamos el flag si el efecto sigue vivo
        if (!cancelled) {
          isRefreshingAccountRef.current = false;
        }
      }
    }, 200); // 0.2s de margen antes de pedir

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [wsOpenOrders, PrimaryAccountInfo?.account]);

  //---------------------------------------------  CONSTANTS PANEL BLAU ---------------------------------------------

  const hasAccount = Boolean(PrimaryAccountInfo?.account);

  const accountNumber = PrimaryAccountInfo && PrimaryAccountInfo.account;
  const robotActivated = PrimaryAccountInfo.numActivatedRobots > 0;
  const realTradingAccount = PrimaryAccountInfo?.liveServer === "Y";

  const apiBalance = PrimaryAccountInfo?.balance ?? 0;

  const apiEquity =
    PrimaryAccountInfo?.equity != null ? PrimaryAccountInfo.equity : apiBalance; // fallback si el endpoint REST no trae equity

  const apiCurrentProfitPct =
    PrimaryAccountInfo?.currentProfitPct ??
    ((PrimaryAccountInfo?.balance ?? 0) > 0
      ? ((PrimaryAccountInfo?.currentProfitAbs ?? 0) /
          (PrimaryAccountInfo?.balance ?? 1)) *
        100
      : 0);
  const apiOpenOrders = Number(PrimaryAccountInfo?.openOrdersCount ?? 0);

  // 👉 Moneda de la cuenta (EUR, USD, etc.)
  // si el back no manda nada, se queda en "" y no se pinta símbolo
  const accountCurrency = PrimaryAccountInfo?.currency || "";

  const formatAmountWithCurrencyNoSpace = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return "-";

    const formattedNumber = n.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // si no hay currency, no añadimos nada
    if (!accountCurrency) {
      return formattedNumber;
    }

    // símbolo bonito si es EUR, si no usamos el código tal cual (USD, GBP, etc.)
    const symbol = accountCurrency === "EUR" ? "€" : accountCurrency;

    return `${formattedNumber}${symbol}`; // 👈 sin espacio
  };

  // Prioriza datos de WS

  const equity = wsEquity ?? apiEquity; // 👈 NUEVO: equity que usaremos en el panel
  const currentProfitPct =
    wsCurrentProfitPct != null ? wsCurrentProfitPct : apiCurrentProfitPct;
  const openOrders = wsOpenOrders != null ? wsOpenOrders : apiOpenOrders;

  const numberActiveRobots = Number(
    PrimaryAccountInfo?.numActivatedRobots ?? 0
  );

  const lastMonthRentability = `${(
    PrimaryAccountInfo?.lastMonthReturnPct ??
    ((PrimaryAccountInfo?.balance ?? 0) > 0
      ? ((PrimaryAccountInfo?.lastMonthReturnAbs ?? 0) /
          (PrimaryAccountInfo?.balance ?? 1)) *
        100
      : 0)
  ).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

  const Actuallyrentability = `${(Number(currentProfitPct) || 0).toLocaleString(
    "es-ES",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}%`;

  const bestRobot = PrimaryAccountInfo?.bestRobot?.robotName ?? "-"; // mejor robot de la cuenta principal

  const bestRobotProfit = PrimaryAccountInfo?.bestRobot?.profit ?? null; // rentabilidad del mejor robot de la cuenta principal

  const getQuarter = (date = new Date()) => Math.floor(date.getMonth() / 3) + 1;

  const getQuarterLabel = (quarter) =>
    t(`dashboard:quarters.q${quarter}`, `Q${quarter}`);

  const getTournamentCompetitionTitle = (date = new Date()) => {
    const year = date.getFullYear();
    const quarter = getQuarter(date);
    const quarterLabel = getQuarterLabel(quarter);

    return t("dashboard:competition.title", {
      quarterLabel,
      year,
      defaultValue: `Competición ${quarterLabel} de ${year}`,
    });
  };

  // ✅ Ruta placeholder (cámbiala cuando tengas la definitiva)
  const COMPETITION_INFO_ROUTE =
    "https://help.tradeasy.tech/docs/competicion-trimestral/";
  const COMPETITION_JOIN_URL = "https://web.tradeasy.tech/torneo";

  const compactActionButtonSx = {
    flex: 1,
    minWidth: 0,
    fontSize: "0.75rem",
    py: 0.45,
    px: 1,
    borderRadius: "10px",
    justifyContent: "center",
    whiteSpace: "nowrap",
  };

  const compactSecondaryButtonSx = {
    ...compactActionButtonSx,
    backgroundColor: "#FFFFFF",
    color: "#253E66",
    border: "1px solid #D8DCE6",
    "&:hover": {
      backgroundColor: "#F7F7FA",
    },
  };

  const handleOpenDashboardMenu = (event, item, fallbackKind, title) => {
    openTopMenu(event, {
      ...item,
      kind: detectKind(item, fallbackKind),
      title: title || getTitle(item),
      shared: item?.shared ?? item?.clientShared ?? 1,
    });
  };

  const handleVincularCuenta = () => {
    dispatch(resetBuilder());
    dispatch(reset());
    setLoadDestination("/tradingrobots/main");
    setLoaded(true);
  };

  if (loadingScreen) {
    return <LoadingShow />;
  }

  if (loaded) {
    return <Redirect to={loadDestination} push />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        minHeight: "100%",
        height: { lg: "100vh", xs: "auto" },
        width: "100%",
        padding: {
          xs: "0 0 12px 0",
          sm: "0 0 12px 0",
          lg: "0",
        },
        overflowY: { lg: "auto", xs: "visible" },
      }}
    >
      {/* ==================== FILA SUPERIOR ==================== */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        sx={{
          // En escritorio seguimos con 55% fijo,
          // en móvil que mida LO QUE NECESITE (no que intente flexear)
          flex: { lg: "0 0 55%", xs: "0 0 auto" },
          gap: 1,
          minHeight: 0,
        }}
      >
        {/* -------- Panel azul: Cuenta principal -------- */}
        <Box
          sx={{
            flex: { lg: "0 0 40%", xs: "1 1 auto" },
            minWidth: 0,
            display: "flex",
          }}
        >
          <Box
            bgcolor="#253E66"
            borderRadius="10px"
            display="flex"
            flexDirection="column"
            sx={{
              width: "100%",
              height: "100%",
              p: 2,
            }}
          >
            {/* Título */}
            <Typography
              component="h2"
              variant="h5"
              color="primary"
              sx={{
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              {t("dashboard:summary")}
            </Typography>

            {/* Cuenta vinculada / vincular */}
            {primaryAccountLoading ? (
              // 🔵 Mientras se carga: spinner centrado en el panel azul
              <Box
                sx={{
                  flex: 1, // ocupa todo el alto disponible del panel
                  display: "flex",
                  alignItems: "center", // centra vertical
                  justifyContent: "center", // centra horizontal
                }}
              >
                <CircularProgress size={32} sx={{ color: "#FFFFFF" }} />
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                gap={1}
                mb={1}
              >
                {accountNumber ? (
                  <Typography color="secondary" variant="subtitle1" noWrap>
                    {t("dashboard:linkedAccountLabel", "Cuenta:")}&nbsp;
                    <Typography
                      component="span"
                      variant="inherit"
                      fontWeight={600}
                      fontFamily="Poppins"
                    >
                      {accountNumber}
                    </Typography>
                  </Typography>
                ) : (
                  <>
                    <Typography
                      color="secondary"
                      variant="subtitle1"
                      sx={{ flex: 1 }}
                    >
                      {t(
                        "dashboard:noLinkedAccount",
                        "No tienes ninguna cuenta vinculada"
                      )}
                    </Typography>
                    <Button
                      onClick={handleVincularCuenta}
                      endIcon={<AddCircleOutlineIcon />}
                      size="small"
                      sx={{
                        width: isCompactLayout ? "100%" : "auto",
                        justifyContent: isCompactLayout ? "space-between" : "center",
                        whiteSpace: "nowrap",
                        border: "1px solid #fff",
                        color: "#fff",
                        bgcolor: "#25AAE2",
                        "&:hover": {
                          bgcolor: "#1b8ec0",
                        },
                      }}
                    >
                      {t("dashboard:linkAccount", "Vincular")}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Estado de robots / tipo de cuenta */}
            {hasAccount && (
              <>
                {/* Robots activos / activar */}
                <Box
                  display="flex"
                  alignItems={isCompactLayout ? "stretch" : "center"}
                  flexDirection={isCompactLayout ? "column" : "row"}
                  gap={1}
                  mb={1}
                >
                  {robotActivated ? (
                    <Typography color="secondary" variant="subtitle1">
                      {t("dashboard:robotActive", "Robot activado")}
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        color="secondary"
                        variant="subtitle1"
                        sx={{ flex: 1 }}
                      >
                        {t(
                          "dashboard:noActiveRobots",
                          "No tienes robots activos"
                        )}
                      </Typography>
                      <Button
                        onClick={handleVincularCuenta}
                        endIcon={<AddCircleOutlineIcon />}
                        size="small"
                        sx={{
                          width: isCompactLayout ? "100%" : "auto",
                          justifyContent:
                            isCompactLayout ? "space-between" : "center",
                          whiteSpace: "nowrap",
                          border: "1px solid #fff",
                          color: "#fff",
                          bgcolor: "#25AAE2",
                          "&:hover": {
                            bgcolor: "#1b8ec0",
                          },
                        }}
                      >
                        {t("dashboard:activateRobots", "Activar")}
                      </Button>
                    </>
                  )}
                </Box>

                {/* Cuenta real / simulada SOLO si hay robots activos */}
                {robotActivated && (
                  <Box
                    display="flex"
                    alignItems={isCompactLayout ? "stretch" : "center"}
                    flexDirection={isCompactLayout ? "column" : "row"}
                    gap={1}
                    mb={1.5}
                  >
                    {realTradingAccount ? (
                      <Typography color="secondary" variant="subtitle1">
                        {t("dashboard:liveEnabled", "Cuenta real activada")}
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          color="secondary"
                          variant="subtitle1"
                          sx={{ flex: 1 }}
                        >
                          {t("dashboard:simAccount", "Cuenta en simulado")}
                        </Typography>
                        <Button
                          onClick={() => {
                            dispatch(resetBuilder());
                            dispatch(reset());
                            setLoadDestination("/tradingrobots/linkAccount");
                            setLoaded(true);
                          }}
                          endIcon={<AddCircleOutlineIcon />}
                          size="small"
                          sx={{
                            width: isCompactLayout ? "100%" : "auto",
                            justifyContent:
                              isCompactLayout ? "space-between" : "center",
                            whiteSpace: "nowrap",
                            border: "1px solid #fff",
                            color: "#fff",
                            bgcolor: "#25AAE2",
                            "&:hover": {
                              bgcolor: "#1b8ec0",
                            },
                          }}
                        >
                          {t("dashboard:switchToLive", "Pasar a real")}
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* Línea separadora */}
            {hasAccount && (
              <Box
                sx={{
                  width: "40%",
                  height: 3,
                  minHeight: 3, // 👈 evita que se colapse
                  flexShrink: 0, // 👈 no permitir que flex la aplaste
                  bgcolor: "#FFFFFF",
                  borderRadius: 999,
                  mx: "auto",
                  mb: 1.2,
                }}
              />
            )}

            {/* ===== Wrapper de métricas para repartirlas homogéneamente ===== */}
            {hasAccount && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0.6, // pequeño gap constante entre bloques
                }}
              >
                {/* === BLOQUE 1: SALDO ACTUAL / RENTABILIDAD ACUMULADA === */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isCompactLayout
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  {/* Saldo actual (equity) */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="secondary" // ← blanco
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem", // 👈 aquí subes/bajas el tamaño
                      }}
                    >
                      {t("dashboard:currentBalance", "SALDO ACTUAL")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: "1.05rem",
                      }}
                    >
                      {formatAmountWithCurrencyNoSpace(equity)}
                    </Typography>
                  </Box>

                  {/* Rentabilidad acumulada (último mes) */}
                  <Box sx={{ textAlign: isCompactLayout ? "left" : "right" }}>
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem", // 👈 igual
                      }}
                    >
                      {t("dashboard:cumProfit", "RENTABILIDAD ACUMULADA")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color:
                          (PrimaryAccountInfo?.lastMonthReturnPct ??
                            ((PrimaryAccountInfo?.balance ?? 0) > 0
                              ? ((PrimaryAccountInfo?.lastMonthReturnAbs ?? 0) /
                                  (PrimaryAccountInfo?.balance ?? 1)) *
                                100
                              : 0)) >= 0
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      {lastMonthRentability}
                    </Typography>
                  </Box>
                </Box>

                {/* === BLOQUE 2: RENTABILIDAD ACTUAL / MEJOR ROBOT === */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isCompactLayout
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  {/* Rentabilidad actual (WS) */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem",
                      }}
                    >
                      {t("dashboard:currentProfit", "RENTABILIDAD ACTUAL")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color:
                          (Number(currentProfitPct) || 0) >= 0
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      {Actuallyrentability}
                    </Typography>
                  </Box>

                  {/* Mejor robot (solo nombre) */}
                  <Box
                    sx={{
                      textAlign: isCompactLayout ? "left" : "right",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem",
                      }}
                    >
                      {t("dashboard:bestRobot", "MEJOR ROBOT")}
                    </Typography>
                    <Typography
                      variant="body1"
                      noWrap
                      title={bestRobot}
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {bestRobot}
                    </Typography>
                  </Box>
                </Box>

                {/* === BLOQUE 3: ÓRDENES ABIERTAS / ROBOT CON MAYOR BENEFICIO === */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isCompactLayout
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    columnGap: 1.5,
                    rowGap: 1,
                  }}
                >
                  {/* Órdenes abiertas */}
                  <Box>
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem",
                      }}
                    >
                      {t("dashboard:openOrders", "ÓRDENES ABIERTAS")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: "1.05rem",
                      }}
                    >
                      {Number.isFinite(openOrders) ? openOrders : 0}
                    </Typography>
                  </Box>

                  {/* Robot con mayor beneficio (importe) */}
                  <Box sx={{ textAlign: isCompactLayout ? "left" : "right" }}>
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem",
                      }}
                    >
                      {t(
                        "dashboard:bestRobotProfit",
                        "ROBOT CON MAYOR BENFICIO"
                      )}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color:
                          (Number(bestRobotProfit) ?? 0) >= 0
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      {formatAmountWithCurrencyNoSpace(bestRobotProfit)}
                    </Typography>
                  </Box>
                </Box>

                {/* === BLOQUE 4: ROBOTS ACTIVOS + botón "Ir a mi cuenta" === */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isCompactLayout ? "column" : "row",
                    alignItems: isCompactLayout ? "stretch" : "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="secondary"
                      sx={{
                        opacity: 0.9,
                        fontSize: "0.85rem",
                      }}
                    >
                      {t("dashboard:activeRobots", "ROBOTS ACTIVOS")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 700,
                        fontSize: "1.05rem",
                      }}
                    >
                      {Number.isFinite(numberActiveRobots)
                        ? numberActiveRobots
                        : 0}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: isCompactLayout ? "left" : "right" }}>
                    <Button
                      onClick={handleVincularCuenta}
                      endIcon={<ArrowForwardIcon />}
                      size="small"
                      sx={{
                        width: isCompactLayout ? "100%" : "auto",
                        justifyContent:
                          isCompactLayout ? "space-between" : "center",
                        borderRadius: "10px",
                        textTransform: "none",
                        border: "1px solid #A7ADB8",
                        color: "#FFFFFF",
                        px: 1.3,
                        py: 0.3,
                      }}
                    >
                      {t("dashboard:goToMyAccount", "Ir a mi cuenta")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* -------- Competition summary -------- */}
        <DashboardCompetitionCard
          title={getTournamentCompetitionTitle()}
          items={rankingTop3}
          lastUpdated={rankingTop3LastUpdated}
          onOpenMenu={(event, item) =>
            handleOpenDashboardMenu(
              event,
              item,
              item.kind,
              item.title || item.robotName || item.strategyName
            )
          }
          infoUrl={COMPETITION_INFO_ROUTE}
          joinUrl={COMPETITION_JOIN_URL}
        />

        {/* -------- Today's economic calendar -------- */}
        <DashboardEconomicCalendarCard />
      </Box>

      {/* ==================== FILA INFERIOR (3 paneles iguales) ==================== */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        sx={{
          flex: { lg: "0 0 45%", xs: "0 0 auto" },
          gap: 1,
          minHeight: 0,
          alignItems: "stretch",
        }}
      >
        {/* -------- Panel 1: Compartidos robots/estrategias -------- */}
        <Box sx={{ flex: 1.1, minWidth: 0, display: "flex" }}>
          <Box
            bgcolor="#FFF"
            borderRadius="10px"
            p={1.5}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              display="flex"
              alignItems={isCompactLayout ? "stretch" : "center"}
              flexDirection={isCompactLayout ? "column" : "row"}
              justifyContent="space-between"
              gap={1}
              mb={0.5}
            >
              <Typography
                variant="h6"
                color="textSecondary"
                fontWeight={500}
                fontFamily="Catamaran"
              >
                {sharedHeaderTitle}
              </Typography>
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                gap={1}
                width={isCompactLayout ? "100%" : "auto"}
              >
                <Button
                  component={Link}
                  to={sharedHeaderCta}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    px: 1,
                    py: 0.3,
                    borderRadius: "6px",
                    width: isCompactLayout ? "100%" : "auto",
                    justifyContent: isCompactLayout ? "space-between" : "center",
                    backgroundColor: "#25AAE2",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#1b8ec0" },
                  }}
                  endIcon={
                    <ArrowForwardIcon sx={{ fontSize: 18, color: "#FFFFFF" }} />
                  }
                >
                  {t("dashboard:viewAll", "Ver todo")}
                </Button>
                <Tabs
                  value={sortType}
                  onChange={handleSortChange}
                  variant={isCompactLayout ? "fullWidth" : "standard"}
                  sx={{
                    minHeight: 0,
                    backgroundColor: "#F7F7FA",
                    borderRadius: "5px",
                    width: isCompactLayout ? "100%" : "auto",
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .Mui-selected": {
                      backgroundColor: "#25AAE2",
                      color: "#FFFFFF !important",
                    },
                  }}
                >
                  <Tab
                    value="robots"
                    label={t("dashboard:robots")}
                    sx={{ minHeight: 0, minWidth: 0, p: 0.4 }}
                  />
                  <Tab
                    value="strategies"
                    label={t("dashboard:strategies")}
                    sx={{ minHeight: 0, minWidth: 0, p: 0.4 }}
                  />
                </Tabs>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 0.5,
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  height: isCompactLayout ? "auto" : "100%",
                }}
              >
                {[
                  ...(sortType === "robots"
                    ? sharedRobots ?? []
                    : sharedStrategies ?? []),
                ]
                  .sort((a, b) => {
                    const timeOf = (x) =>
                      new Date(
                        x?.sharedDate ??
                          x?.shared_date ??
                          x?.created_at ??
                          x?.createdAt ??
                          x?.updated_at ??
                          0
                      ).getTime();
                    return timeOf(b) - timeOf(a);
                  })
                  .slice(0, 3)
                  .map((item) => {
                    const isRobotsTab = sortType === "robots";
                    const title =
                      item?.title ?? item?.name ?? t("dashboard:untitled");
                    const tfName =
                      item?.tf?.name ??
                      item?.timeframe?.name ??
                      item?.interval ??
                      item?.tf;
                    const tfLabel = tfName
                      ? t?.(`timeframes:${tfName}`) || String(tfName)
                      : "undefined";
                    const assetLabel =
                      item?.ticker?.display_name ??
                      item?.asset?.display_name ??
                      item?.symbol_name ??
                      item?.symbol ??
                      item?.asset ??
                      item?.ticker?.symbol ??
                      "undefined";
                    const tradiEasyScore =
                      item?.tradEasyScore ??
                      item?.tradeasyScore ??
                      item?.teScore ??
                      item?.score ??
                      null;
                    const createdRaw =
                      item?.sharedDate ??
                      item?.shared_date ??
                      item?.created_at ??
                      item?.createdAt ??
                      item?.updated_at ??
                      null;
                    const createdFmt = createdRaw
                      ? format(new Date(createdRaw), "dd.MM.yyyy")
                      : "—";

                    return (
                      <Box
                        key={item.id}
                        sx={{
                          flex: isCompactLayout ? "0 0 auto" : 1,
                          minHeight: 0,
                          display: "flex",
                        }}
                      >
                        <Box
                          borderRadius="6px"
                          bgcolor="#F7F7FA"
                          p={0.75}
                          position="relative"
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Overlay acciones */}
                          <Box
                            position={isCompactLayout ? "static" : "absolute"}
                            top={isCompactLayout ? "auto" : 0}
                            left={isCompactLayout ? "auto" : 0}
                            right={isCompactLayout ? "auto" : 0}
                            bottom={isCompactLayout ? "auto" : 0}
                            sx={{
                              opacity: isCompactLayout ? 1 : 0,
                              transition: isCompactLayout
                                ? "none"
                                : (theme) => theme.transitions.create("opacity"),
                              "&:hover": { opacity: 1 },
                              bgcolor: isCompactLayout
                                ? "transparent"
                                : "rgba(235,235,242,0.9)",
                              borderRadius: isCompactLayout ? 0 : "6px",
                              zIndex: 1,

                              // 👇 MUY IMPORTANTE: el overlay se convierte en un contenedor flex
                              display: "flex",
                              alignItems: isCompactLayout ? "stretch" : "center",
                              justifyContent: "center",
                              mt: isCompactLayout ? 1 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: "100%",
                                px: isCompactLayout ? 0 : 2,
                                display: "flex",
                                flexDirection: isCompactLayout ? "row" : "column",
                                flexWrap: "wrap",
                                gap: 0.75,
                              }}
                            >
                              {/* Builder solo para estrategias */}
                              {!isRobotsTab && (
                                <>
                                  {isUserFree ? (
                                    <ProtectedComponent
                                      iconTop="-30px"
                                      iconRight="2px"
                                      iconSize="20"
                                      iconColor="grey"
                                    >
                                      <Button
                                        size="small"
                                        fullWidth // 👈 ocupa TODO el ancho del contenedor
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                          ...(isCompactLayout
                                            ? compactSecondaryButtonSx
                                            : {
                                                fontSize: "0.8rem",
                                                py: 0.4,
                                                justifyContent: "center",
                                              }),
                                        }}
                                      >
                                        {t("dashboard:builder")}
                                      </Button>
                                    </ProtectedComponent>
                                  ) : (
                                    <LoadingButton
                                      size="small"
                                      fullWidth // 👈 lo mismo aquí
                                      loading={ctaLoading}
                                      variant="contained"
                                      loadingPosition="end"
                                      endIcon={<ArrowForwardIcon />}
                                      onClick={handleSharedLoad(
                                        item.id,
                                        "/builder"
                                      )}
                                      sx={{
                                        ...(isCompactLayout
                                          ? compactSecondaryButtonSx
                                          : {
                                              fontSize: "0.8rem",
                                              py: 0.4,
                                              justifyContent: "center",
                                            }),
                                      }}
                                    >
                                      {t("dashboard:builder")}
                                    </LoadingButton>
                                  )}
                                </>
                              )}

                              {/* Validar */}
                              {isUserFree ? (
                                <ProtectedComponent
                                  iconTop="-30px"
                                  iconRight="2px"
                                  iconSize="20"
                                  iconColor="grey"
                                >
                                  <Button
                                    size="small"
                                    fullWidth // 👈 también a todo el ancho
                                    endIcon={<TimelineIcon />}
                                    sx={{
                                      ...(isCompactLayout
                                        ? compactActionButtonSx
                                        : {
                                            fontSize: "0.8rem",
                                            py: 0.4,
                                            justifyContent: "center",
                                          }),
                                    }}
                                  >
                                    {t("dashboard:validate")}
                                  </Button>
                                </ProtectedComponent>
                              ) : (
                                <LoadingButton
                                  size="small"
                                  fullWidth // 👈 igual
                                  variant="contained"
                                  loading={ctaLoading}
                                  loadingPosition="end"
                                  endIcon={<TimelineIcon />}
                                  onClick={handleSharedLoad(
                                    item.id,
                                    "/validation"
                                  )}
                                  sx={{
                                    ...(isCompactLayout
                                      ? compactActionButtonSx
                                      : {
                                          fontSize: "0.8rem",
                                          py: 0.4,
                                          justifyContent: "center",
                                        }),
                                  }}
                                >
                                  {t("dashboard:validate")}
                                </LoadingButton>
                              )}
                              {isCompactLayout && (
                                <IconButton
                                  size="small"
                                  aria-label={t(
                                    "dashboard:moreActions",
                                    "Más acciones"
                                  )}
                                  onClick={(event) =>
                                    handleOpenDashboardMenu(
                                      event,
                                      { ...item, title },
                                      isRobotsTab ? "robot" : "strategy",
                                      title
                                    )
                                  }
                                  sx={{
                                    border: "1px solid #D8DCE6",
                                    borderRadius: "10px",
                                    bgcolor: "#FFFFFF",
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Contenido tarjeta */}
                          <Typography
                            fontFamily="Catamaran"
                            noWrap={!isCompactLayout}
                            variant="body2"
                            sx={{ mb: 0.3 }}
                          >
                            {title}
                          </Typography>

                          <Box
                            display="flex"
                            alignItems={isCompactLayout ? "stretch" : "center"}
                            flexDirection={isCompactLayout ? "column" : "row"}
                            sx={{ mt: 0.2, gap: 1 }}
                          >
                            {/* IZQUIERDA: Profit / Risk */}
                            <ProfitRiskBox
                              profit={getAnnualizedProfit(item)}
                              risk={item.risk}
                              profitLabel="Annual Profit"
                            />

                            {/* CENTRO: Timeframe / Activo / Creada */}
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: isCompactLayout
                                  ? "flex-start"
                                  : "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                }}
                              >
                                {isRobotsTab ? (
                                  // Para robots: solo fecha de creación en el centro
                                  <Typography
                                    color="textSecondary"
                                    variant="caption"
                                    sx={{
                                      whiteSpace: isCompactLayout
                                        ? "normal"
                                        : "nowrap",
                                    }}
                                  >
                                    {t("dashboard:created", "Creada")}:{" "}
                                    <Typography
                                      component="span"
                                      variant="inherit"
                                      color="textPrimary"
                                    >
                                      {createdFmt}
                                    </Typography>
                                  </Typography>
                                ) : (
                                  // Para estrategias: Timeframe / Activo / Creada
                                  <>
                                    <Typography
                                      color="textSecondary"
                                      variant="caption"
                                      sx={{
                                        whiteSpace: isCompactLayout
                                          ? "normal"
                                          : "nowrap",
                                      }}
                                    >
                                      {t("dashboard:timeframe", "Timeframe")}:{" "}
                                      <Typography
                                        component="span"
                                        variant="inherit"
                                        color={
                                          tfLabel !== "undefined"
                                            ? "textPrimary"
                                            : "error"
                                        }
                                      >
                                        {tfLabel}
                                      </Typography>
                                    </Typography>

                                    <Typography
                                      color="textSecondary"
                                      variant="caption"
                                      sx={{
                                        whiteSpace: isCompactLayout
                                          ? "normal"
                                          : "nowrap",
                                      }}
                                    >
                                      {t("dashboard:asset", "Activo")}:{" "}
                                      <Typography
                                        component="span"
                                        variant="inherit"
                                        color={
                                          assetLabel !== "undefined"
                                            ? "textPrimary"
                                            : "error"
                                        }
                                      >
                                        {assetLabel}
                                      </Typography>
                                    </Typography>

                                    <Typography
                                      color="textSecondary"
                                      variant="caption"
                                      sx={{
                                        whiteSpace: isCompactLayout
                                          ? "normal"
                                          : "nowrap",
                                      }}
                                    >
                                      {t("dashboard:created", "Creada")}:{" "}
                                      <Typography
                                        component="span"
                                        variant="inherit"
                                        color="textPrimary"
                                      >
                                        {createdFmt}
                                      </Typography>
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </Box>

                            {/* DERECHA: Score solo */}
                            <Box
                              sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1,
                                py: 0.25,
                                minWidth: isCompactLayout ? "100%" : 70,
                                backgroundColor: "#253E66",
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#FFFFFF",
                                  fontWeight: 600,
                                }}
                              >
                                Score
                              </Typography>
                              <Typography
                                sx={(theme) => ({
                                  fontWeight: 800,
                                  fontSize: 14,
                                  color:
                                    tradiEasyScore != null
                                      ? theme.palette?.success?.main ||
                                        "#2e7d32"
                                      : theme.palette?.error?.main || "#d32f2f",
                                })}
                              >
                                {typeof tradiEasyScore === "number"
                                  ? tradiEasyScore.toFixed(2)
                                  : tradiEasyScore ?? "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* -------- Panel 2: Formación -------- */}

        <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
          <Box
            bgcolor="#FFF"
            borderRadius="10px"
            p={0}
            sx={{
              width: "100%",
              height: isCompactLayout ? "auto" : "99%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              display="flex"
              alignItems={isCompactLayout ? "stretch" : "center"}
              flexDirection={isCompactLayout ? "column" : "row"}
              justifyContent="space-between"
              gap={1}
              mb={0.5}
              sx={{ mt: 1 }} // 👈 BAJA un poco el título y el botón
            >
              <Typography
                variant="h6"
                color="textSecondary"
                fontWeight={500}
                fontFamily="Catamaran"
              >
                {t("dashboard:training", "Formación")}
              </Typography>
              <Button
                size="small"
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 1,
                  py: 0.3,
                  width: isCompactLayout ? "100%" : "auto",
                  justifyContent: isCompactLayout ? "space-between" : "center",
                }}
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => history.push("/myCourses")}
              >
                {t("dashboard:viewAll", "Ver todo")}
              </Button>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              {trainingCourses.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    height: isCompactLayout ? "auto" : "100%",
                  }}
                >
                  {trainingCourses.slice(0, 2).map(({ course }) => (
                    <Box
                      key={course.id}
                      sx={{
                        flex: 1, // 👈 cada curso ocupa la mitad
                        minHeight: 0,
                        display: "flex",
                      }}
                    >
                      <CourseCard
                        course={course}
                        onClick={() => handleCourseClick(course.id)}
                        variant="compact"
                        cardSx={{
                          borderRadius: 3,
                          width: "100%",
                          height: "100%", // 👈 el propio card se estira
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ color: "text.secondary" }}
                >
                  {t("dashboard:noCourses", "No tienes cursos aún")}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* -------- Panel 3: Otros / Ayuda -------- */}

        <Box sx={{ flex: 0.9, minWidth: 0, display: "flex" }}>
          <Box
            bgcolor="#FFF"
            borderRadius="10px"
            p={1.5}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Typography
              variant="h6"
              color="textSecondary"
              fontWeight={500}
              fontFamily="Catamaran"
              mb={0.5}
            >
              {t("dashboard:others", "Otros")}
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                height: "100%", // 👈 todo el alto del panel
              }}
            >
              {/* Telegram */}
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                sx={{
                  gap: 1,
                  px: 1,
                  borderRadius: "8px",
                  bgcolor: "#F7F7FA",
                  flex: 1, // 👈 las 4 cajas se reparten el alto
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Catamaran", fontWeight: 500 }}
                  noWrap={!isCompactLayout}
                >
                  {t(
                    "dashboard:joinTelegram",
                    "Únete a nuestro grupo de Telegram"
                  )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  component="a"
                  href="https://t.me/tradeasy_tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    px: 1.4,
                    py: 0.4,
                    borderRadius: "10px",
                    width: isCompactLayout ? "100%" : "auto",
                    justifyContent: isCompactLayout ? "space-between" : "center",
                    backgroundColor: "#25AAE2",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#1b8ec0" },
                  }}
                >
                  {t("dashboard:go", "Ir")}
                </Button>
              </Box>

              {/* Blog */}
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                sx={{
                  gap: 1,
                  px: 1,
                  borderRadius: "8px",
                  bgcolor: "#F7F7FA",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Catamaran", fontWeight: 500 }}
                  noWrap={!isCompactLayout}
                >
                  {t(
                    "dashboard:checkBlog",
                    "Revisa nuestros artículos del blog"
                  )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  component="a"
                  href="https://web.tradeasy.tech/blog/"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    px: 1.4,
                    py: 0.4,
                    borderRadius: "10px",
                    width: isCompactLayout ? "100%" : "auto",
                    justifyContent: isCompactLayout ? "space-between" : "center",
                    backgroundColor: "#25AAE2",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#1b8ec0" },
                  }}
                >
                  {t("dashboard:go", "Ir")}
                </Button>
              </Box>

              {/* YouTube */}
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                sx={{
                  gap: 1,
                  px: 1,
                  borderRadius: "8px",
                  bgcolor: "#F7F7FA",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Catamaran", fontWeight: 500 }}
                  noWrap={!isCompactLayout}
                >
                  {t(
                    "dashboard:visitYoutube",
                    "Visita nuestro canal de YouTube"
                  )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  component="a"
                  href="https://www.youtube.com/@tradEAsytech"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    px: 1.4,
                    py: 0.4,
                    borderRadius: "10px",
                    width: isCompactLayout ? "100%" : "auto",
                    justifyContent: isCompactLayout ? "space-between" : "center",
                    backgroundColor: "#25AAE2",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#1b8ec0" },
                  }}
                >
                  {t("dashboard:go", "Ir")}
                </Button>
              </Box>

              {/* Help Center */}
              <Box
                display="flex"
                alignItems={isCompactLayout ? "stretch" : "center"}
                flexDirection={isCompactLayout ? "column" : "row"}
                sx={{
                  gap: 1,
                  px: 1,
                  borderRadius: "8px",
                  bgcolor: "#F7F7FA",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Catamaran", fontWeight: 500 }}
                  noWrap={!isCompactLayout}
                >
                  {t(
                    "dashboard:helpCenter",
                    "Accede a nuestro centro de ayuda"
                  )}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  component="a"
                  href="https://help.tradeasy.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    px: 1.4,
                    py: 0.4,
                    borderRadius: "10px",
                    width: isCompactLayout ? "100%" : "auto",
                    justifyContent: isCompactLayout ? "space-between" : "center",
                    backgroundColor: "#25AAE2",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#1b8ec0" },
                  }}
                >
                  {t("dashboard:go", "Ir")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <DashboardTopMenu
        anchor={menuAnchorTop}
        open={Boolean(menuAnchorTop && selectedTopItem)}
        onClose={closeTopMenu}
        id={selectedTopItem?.id}
        kind={selectedTopItem?.kind}
        title={selectedTopItem?.title || getTitle(selectedTopItem)}
        shared={selectedTopItem?.shared ?? selectedTopItem?.clientShared ?? 1}
        activeAPI={selectedTopItem?.activeAPI}
        robotAlwaysActive={selectedTopItem?.robotAlwaysActive}
        userId={userId}
      />
    </Box>
  );
};

export default Dashboard;
